import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Calendar, Mail, Phone, MapPin, Users, Loader2, DollarSign } from "lucide-react";
import AddOnsModal from "@/components/AddOnsModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const [inquiries, setInquiries] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [showAddOnsModal, setShowAddOnsModal] = useState(false);
  const [currentInquiryForPayment, setCurrentInquiryForPayment] = useState(null);
  const [consultationForm, setConsultationForm] = useState({
    scheduled_date: "",
    scheduled_time: "",
    notes: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [inquiriesRes, consultationsRes] = await Promise.all([
        axios.get(`${API}/inquiries`),
        axios.get(`${API}/consultations`),
      ]);
      setInquiries(inquiriesRes.data);
      setConsultations(consultationsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (inquiryId, newStatus) => {
    try {
      await axios.put(`${API}/inquiries/${inquiryId}`, { status: newStatus });
      toast.success("Status updated successfully");
      fetchData();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const handleScheduleConsultation = async (inquiryId) => {
    try {
      await axios.post(`${API}/consultations`, {
        inquiry_id: inquiryId,
        ...consultationForm,
      });
      toast.success("Consultation scheduled successfully");
      setConsultationForm({ scheduled_date: "", scheduled_time: "", notes: "" });
      fetchData();
    } catch (error) {
      console.error("Error scheduling consultation:", error);
      toast.error("Failed to schedule consultation");
    }
  };

  const initiatePayment = async (inquiryId) => {
    const inquiry = inquiries.find(i => i.id === inquiryId);
    setCurrentInquiryForPayment(inquiry);
    setShowAddOnsModal(true);
  };

  const handleAddOnsConfirm = async (addOnsData) => {
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/payments/checkout`, {
        inquiry_id: currentInquiryForPayment.id,
        origin_url: originUrl,
        add_ons: addOnsData.addOns,
        total_amount: addOnsData.total,
      });
      
      // Open Stripe checkout in new tab
      window.open(response.data.url, '_blank');
      toast.success("Payment link generated with add-ons. Opening in new tab...");
      
      setShowAddOnsModal(false);
      setCurrentInquiryForPayment(null);
    } catch (error) {
      console.error("Error creating payment:", error);
      toast.error("Failed to generate payment link");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "consultation_scheduled":
        return "bg-blue-100 text-blue-800";
      case "deposit_paid":
        return "bg-green-100 text-green-800";
      case "confirmed":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-16" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <h1 className="text-5xl font-bold mb-12" data-testid="admin-heading">
          Admin Dashboard
        </h1>

        {/* Inquiries Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8">Inquiries ({inquiries.length})</h2>
          <div className="space-y-6">
            {inquiries.length === 0 ? (
              <p className="text-[#8C8C8C]" data-testid="no-inquiries-message">No inquiries yet.</p>
            ) : (
              inquiries.map((inquiry) => (
                <div
                  key={inquiry.id}
                  data-testid={`inquiry-card-${inquiry.id}`}
                  className="bg-white p-6 rounded-lg shadow-sm border border-[#F2EBE5]"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold mb-1">{inquiry.name}</h3>
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(inquiry.status)}`}>
                        {inquiry.status.replace("_", " ").toUpperCase()}
                      </span>
                    </div>
                    <span className="text-sm text-[#8C8C8C]">
                      {new Date(inquiry.created_at).toLocaleDateString()}
                    </span>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="flex items-center gap-2 text-sm text-[#8C8C8C]">
                      <Mail className="w-4 h-4" />
                      {inquiry.email}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[#8C8C8C]">
                      <Phone className="w-4 h-4" />
                      {inquiry.phone}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[#8C8C8C]">
                      <Calendar className="w-4 h-4" />
                      {inquiry.event_date} at {inquiry.event_time}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[#8C8C8C]">
                      <MapPin className="w-4 h-4" />
                      {inquiry.location}
                    </div>
                  </div>

                  <div className="mb-4">
                    <p className="text-sm mb-2">
                      <strong>Service:</strong> {inquiry.service_category} - {inquiry.service_type}
                    </p>
                    <p className="text-sm mb-2">
                      <strong>Guests:</strong> {inquiry.guest_count}
                    </p>
                    <p className="text-sm mb-2">
                      <strong>Consultation Method:</strong> {inquiry.consultation_method}
                    </p>
                    {inquiry.inspiration_notes && (
                      <p className="text-sm text-[#8C8C8C]">
                        <strong>Notes:</strong> {inquiry.inspiration_notes}
                      </p>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <Select
                      onValueChange={(value) => handleStatusUpdate(inquiry.id, value)}
                      value={inquiry.status}
                    >
                      <SelectTrigger data-testid={`status-select-${inquiry.id}`} className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="consultation_scheduled">Consultation Scheduled</SelectItem>
                        <SelectItem value="deposit_paid">Deposit Paid</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                      </SelectContent>
                    </Select>

                    <Dialog>
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          data-testid={`schedule-consultation-button-${inquiry.id}`}
                          onClick={() => setSelectedInquiry(inquiry.id)}
                        >
                          Schedule Consultation
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Schedule Consultation</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="scheduled_date">Date</Label>
                            <Input
                              id="scheduled_date"
                              type="date"
                              value={consultationForm.scheduled_date}
                              onChange={(e) =>
                                setConsultationForm((prev) => ({
                                  ...prev,
                                  scheduled_date: e.target.value,
                                }))
                              }
                            />
                          </div>
                          <div>
                            <Label htmlFor="scheduled_time">Time</Label>
                            <Input
                              id="scheduled_time"
                              type="time"
                              value={consultationForm.scheduled_time}
                              onChange={(e) =>
                                setConsultationForm((prev) => ({
                                  ...prev,
                                  scheduled_time: e.target.value,
                                }))
                              }
                            />
                          </div>
                          <div>
                            <Label htmlFor="notes">Notes</Label>
                            <Input
                              id="notes"
                              value={consultationForm.notes}
                              onChange={(e) =>
                                setConsultationForm((prev) => ({
                                  ...prev,
                                  notes: e.target.value,
                                }))
                              }
                            />
                          </div>
                          <Button
                            onClick={() => handleScheduleConsultation(selectedInquiry)}
                            className="w-full bg-[#D4AF37] hover:bg-[#b89530]"
                          >
                            Confirm Schedule
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>

                    <Button
                      onClick={() => initiatePayment(inquiry.id)}
                      data-testid={`send-payment-button-${inquiry.id}`}
                      className="bg-[#D4AF37] hover:bg-[#b89530]"
                    >
                      <DollarSign className="w-4 h-4 mr-2" />
                      Send Payment Link
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Consultations Section */}
        <div>
          <h2 className="text-3xl font-bold mb-8">Scheduled Consultations ({consultations.length})</h2>
          <div className="space-y-6">
            {consultations.length === 0 ? (
              <p className="text-[#8C8C8C]">No consultations scheduled yet.</p>
            ) : (
              consultations.map((consultation) => {
                const inquiry = inquiries.find((i) => i.id === consultation.inquiry_id);
                return (
                  <div
                    key={consultation.id}
                    className="bg-white p-6 rounded-lg shadow-sm border border-[#F2EBE5]"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-bold mb-1">{inquiry?.name}</h3>
                        <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {consultation.status.toUpperCase()}
                        </span>
                      </div>
                      <span className="text-sm text-[#8C8C8C]">
                        {consultation.scheduled_date} at {consultation.scheduled_time}
                      </span>
                    </div>
                    {inquiry && (
                      <div className="text-sm text-[#8C8C8C] mb-2">
                        <strong>Contact:</strong> {inquiry.email} | {inquiry.phone}
                      </div>
                    )}
                    {consultation.notes && (
                      <p className="text-sm text-[#8C8C8C]">
                        <strong>Notes:</strong> {consultation.notes}
                      </p>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
      
      {/* Add-ons Modal */}
      <AddOnsModal
        isOpen={showAddOnsModal}
        onClose={() => {
          setShowAddOnsModal(false);
          setCurrentInquiryForPayment(null);
        }}
        onConfirm={handleAddOnsConfirm}
        inquiry={currentInquiryForPayment}
      />
      {/* Add-ons Modal */}
      {currentInquiryForPayment && (
        <AddOnsModal
          isOpen={showAddOnsModal}
          onClose={() => {
            setShowAddOnsModal(false);
            setCurrentInquiryForPayment(null);
          }}
          onConfirm={handleAddOnsConfirm}
          baseAmount={100}
          serviceName={`${currentInquiryForPayment.service_category} - ${currentInquiryForPayment.service_type}`}
        />
      )}
    </div>
  );
};

export default AdminPage;